/* eslint react-hooks/exhaustive-deps: 0 */
import type { RefObject } from 'react';
import { useRef, useState, useCallback, useEffect } from 'react';

import {
	confluenceLocalStorageInstance as localStorage,
	keys as localStorageKeys,
} from '@confluence/storage-manager';

type UseRightPanelResizeOptions = {
	rightPanelRef: RefObject<HTMLElement>;
	defaultWidth: number;
	minWidth: number;
	maxWidth: number;
	compactThreshold: number;
};

export const RIGHT_PANEL_COMPACT_CLASS = 'right-panel-compact';

export const useRightPanelResize = ({
	rightPanelRef,
	defaultWidth,
	minWidth,
	maxWidth,
	compactThreshold,
}: UseRightPanelResizeOptions) => {
	const totalOffset = useRef(0);
	const [initialWidth, setInitialWidth] = useState(
		parseInt(localStorage.getItem(localStorageKeys.HOME_SIDEBAR_WIDTH)) || defaultWidth,
	);

	const applyCompactStyling = () => {
		if (!rightPanelRef.current) return;

		if (rightPanelRef.current.offsetWidth < compactThreshold) {
			rightPanelRef.current.classList.add(RIGHT_PANEL_COMPACT_CLASS);
		} else {
			rightPanelRef.current.classList.remove(RIGHT_PANEL_COMPACT_CLASS);
		}
	};

	const onResizeDrag = useCallback(
		(offset: any) => {
			totalOffset.current += offset;
			const newWidth = Math.max(Math.min(initialWidth - totalOffset.current, maxWidth), minWidth);

			requestAnimationFrame(() => {
				rightPanelRef.current!.style.setProperty('width', `${newWidth}px`);
				applyCompactStyling();
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[rightPanelRef, totalOffset, initialWidth],
	);

	const onResizeComplete = useCallback(() => {
		// Tests require this fallback to style.width because jsdom doesn't give elements an actual width
		const finalWidth =
			rightPanelRef.current!.offsetWidth || parseInt(rightPanelRef.current!.style.width || '');

		localStorage.setItem(localStorageKeys.HOME_SIDEBAR_WIDTH, finalWidth);
		setInitialWidth(finalWidth);

		totalOffset.current = 0;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [totalOffset, localStorage]);

	useEffect(() => {
		applyCompactStyling();

		const resizeListener = () => {
			applyCompactStyling();
		};

		window.addEventListener('resize', resizeListener);
		return () => window.removeEventListener('resize', resizeListener);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		rightPanelRef,
		onResizeDrag,
		onResizeComplete,
		initialWidth,
	};
};
